import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useEffect, useMemo, useState } from 'react'
import { baseUrl } from '../../../../services/baseUrl'
import { constraintConfig } from '../../../../constraintConfig'

type UserInfo = {
    firstName?: string
    lastName?: string
    email?: string
    userEmail?: string
}

const ApprovePortalUserRegistration = () => {
    const { token } = useParams()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isError, setIsError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [userResponse, setUserResponse] = useState<UserInfo>({})

    // Email links: Approve = _2 (Approved), Reject = _3 (Rejected).
    // Older emails used _1 for Approve — treat that as approve for display/compat.
    const actionSuffix = useMemo(() => {
        if (!token) return ''
        const parts = token.split('_')
        return parts.length > 1 ? parts[parts.length - 1] : ''
    }, [token])

    const isReject = actionSuffix === constraintConfig.newUserRequestStatus.Rejected
    const isApprove = !isReject

    useEffect(() => {
        const approveUser = async () => {
            if (!token) {
                toast.error('Invalid approval token')
                setIsError(true)
                setErrorMessage('Missing approval token. The link may be invalid.')
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)
                setIsSuccess(false)
                const loadingToast = toast.loading(
                    isApprove
                        ? 'Approving user account... Please wait.'
                        : 'Rejecting user account... Please wait.'
                )

                const response = await axios.post(
                    `${baseUrl.MAIN_BASE_URL}/api/Account/RequestUserAction?request=${token}`,
                    {},
                    {
                        headers: {
                            accept: '*/*',
                            'Content-Type': 'application/json',
                        },
                    }
                )

                toast.dismiss(loadingToast)
                if (response.data && response.data.success) {
                    toast.success(response.data?.message)
                    const result = response.data?.result
                    if (result && typeof result === 'object') {
                        setUserResponse({
                            firstName: result.firstName || '',
                            lastName: result.lastName || '',
                            email: result.email || result.userEmail || '',
                            userEmail: result.userEmail || result.email || '',
                        })
                    } else {
                        // Reject path may return boolean; keep empty names if not provided
                        setUserResponse({})
                    }
                    setIsSuccess(true)
                } else {
                    toast.error(response.data?.message)
                    setIsError(true)
                    setErrorMessage(response.data?.message || 'Unable to process request.')
                }
            } catch (error: any) {
                toast.dismiss()
                const errorMsg = error?.response?.data?.message || 'Unable to process request.'
                toast.error(errorMsg)
                setIsError(true)
                setErrorMessage(errorMsg)
            } finally {
                setIsLoading(false)
            }
        }

        approveUser()
    }, [token, navigate, isApprove])

    const displayEmail = userResponse?.email || userResponse?.userEmail || 'N/A'
    const displayName = `${userResponse?.firstName || ''} ${userResponse?.lastName || ''}`.trim() || 'N/A'

    // Loading State
    if (isLoading) {
        return (
            <div className='text-center mt-15'>
                <div className='spinner-border text-primary' style={{ width: '3rem', height: '3rem' }}></div>
                <h4 className='mt-6 mb-3'>Processing Request</h4>
                <p className='text-muted'>
                    Please wait while we {isApprove ? 'approve' : 'reject'} this user request.
                </p>
            </div>
        )
    }

    // Success State
    if (isSuccess) {
        return (
            <div className='d-flex justify-content-center align-items-center min-vh-50 p-4'>
                <div className='card shadow-sm border-0' style={{ maxWidth: '500px', width: '100%' }}>
                    <div className='card-body p-6'>
                        <div className='text-center mb-5'>
                            <div className='mb-4'>
                                <span className={`badge badge-lg ${isApprove ? 'badge-success' : 'badge-warning'}`}>
                                    Success
                                </span>
                            </div>

                            <h1 className={`${isApprove ? 'text-success' : 'text-warning'} mb-3 fw-boldest`}>
                                Account {isApprove ? 'Approved' : 'Rejected'} Successfully
                            </h1>

                            <p className='text-gray-600 fs-5 mb-6'>
                                The user account has been successfully {isApprove ? 'approved' : 'rejected'}.
                            </p>
                        </div>

                        <div className='card card-dashed bg-light mb-6'>
                            <div className='card-body p-5'>
                                <div className='row g-4'>
                                    <div className='col-12'>
                                        <div className='d-flex align-items-center mb-3'>
                                            <div className='text-dark fw-bold me-3'>User Name:</div>
                                            <div className='text-gray-500 text-capitalize'>{displayName}</div>
                                        </div>
                                        <div className='d-flex align-items-center'>
                                            <div className='text-dark fw-bold me-3'>Email:</div>
                                            <div className='text-gray-500 text-lowercase'>{displayEmail}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isApprove && (
                            <div className='text-center text-gray-600 fs-5 mb-6'>
                                <p className='mb-0'>
                                    The user has full access based on their assigned role. An activation email has been sent to the user.
                                    The user can follow the instructions in the email to set a new password and then log in to{' '}
                                    <span className='text-dark fw-bold'>SpendSail</span>.
                                </p>
                            </div>
                        )}

                        <div className='d-flex flex-column gap-4'>
                            <button
                                className='btn btn-primary btn-lg fw-bold py-3 border'
                                onClick={() => navigate('/auth/login')}
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // Error State
    return (
        <div className='d-flex justify-content-center align-items-center min-vh-50 p-4'>
            <div className='card shadow-sm border-0' style={{ maxWidth: '450px', width: '100%' }}>
                <div className='card-body p-6 text-center'>
                    <div className='mb-4'>
                        <span className='badge badge-lg badge-danger'>Failure</span>
                    </div>

                    <h3 className='text-danger mb-3 fw-bold'>{isApprove ? 'Approval' : 'Rejection'} Failed</h3>

                    <p className='fw-medium text-gray-800 mb-5'>{errorMessage}</p>

                    <div className='bg-light rounded mb-5 p-4'>
                        <h6 className='fw-bold text-gray-700 mb-3'>Possible reasons:</h6>
                        <div className='text-start'>
                            <div className='d-flex align-items-center mb-2'>
                                <span className='bullet bullet-xs bg-danger me-2'></span>
                                <span className='text-gray-600'>The link has expired</span>
                            </div>
                            <div className='d-flex align-items-center mb-2'>
                                <span className='bullet bullet-xs bg-danger me-2'></span>
                                <span className='text-gray-600'>The request has already been processed</span>
                            </div>
                            <div className='d-flex align-items-center mb-2'>
                                <span className='bullet bullet-xs bg-danger me-2'></span>
                                <span className='text-gray-600'>The token is invalid or malformed</span>
                            </div>
                            <div className='d-flex align-items-center'>
                                <span className='bullet bullet-xs bg-danger me-2'></span>
                                <span className='text-gray-600'>Server connection issues</span>
                            </div>
                        </div>
                    </div>

                    <div className='d-flex justify-content-center gap-3'>
                        <button className='btn btn-primary btn-sm px-4' onClick={() => window.location.reload()}>
                            Try Again
                        </button>
                        <button className='btn btn-warning btn-sm px-4' onClick={() => navigate('/')}>
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ApprovePortalUserRegistration
